import * as React from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';

import ManagedByTag from 'components/managed-by-tag/ManagedByTag';
import MoreButton from 'components/buttons/more-button/MoreButton';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import StatusLabel from 'components/status-label/StatusLabel';
import MetricsChannelsBasic from 'components/metrics/channels-basic/MetricsChannelsBasic';
import PageSection from 'components/page/page-section/PageSection';
import Popover from 'components/popover/Popover';
import PopoverScrollable from 'components/popover-scrollable/PopoverScrollable';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import InfluencerVerificationLabel from 'components/influencer-verification-label/InfluencerVerificationLabel';
import DeliverableTypeCounters from 'modules/deliverables/components/deliverable-type-counters/DeliverableTypeCounters';
import BulkEditOfferModal from '../bulk/bulk-edit-offer-modal/BulkEditOfferModal';
import BulkOfferModal from '../bulk/bulk-offer-modal/BulkOfferModal';
import OfferCardFooter from 'modules/subcampaigns/components/offer-card-footer/OfferCardFooter';

import { __t, applyTranslations } from 'utils/language';
import { prepareAggregatedMetricsData, prepareDataForMetricsChannelsBasic } from 'utils/metrics';
import { renderPrice } from 'utils/helper/price';
import { getManagedByBrand, isInfluencerProfileRejected } from 'utils/profile';
import { addThousandsSeparator } from 'utils/helper/common';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
} from 'utils/channels';
import {
  SUBCAMPAIGN_OFFER_TYPE_OFFER,
  SUBCAMPAIGN_OFFER_TYPE_QUOTE,
  SUBCAMPAIGN_STATE_TO_LABEL_TYPE,
  canCancelSubcampaign,
  isDraft,
  isNegotiationsReopened,
  isRevoked
} from 'utils/subcampaign';
import { canCampaignBeChanged } from 'utils/campaign';
import { getEntityFromProfile } from 'utils/view';
import { insertIf } from 'utils/list';
import { BULK_OFFER_MODAL_MODE_CREATE } from 'utils/bulk-offer';

import type {
  BrandProfile,
  ChannelTypes,
  Deliverable,
  Entity,
  ImageSize,
  Subcampaign,
  SubcampaignInfluencerProfile,
  SubcampaignOffer,
  SubcampaignOfferForm
} from 'types';

import './offer-card-banner.scss';

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: true
});

const CHANNELS: Array<ChannelTypes> = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE];
const MAX_INFLUENCER_COUNT = 30;

applyTranslations(require('./offer-card-banner.i18n.json'));
applyTranslations(require('../copy/subcampaign-offer-copy-paste.i18n.json'));

type Props = {
  canEdit?: boolean,
  triggersOfferUpdate?: boolean,
  influencerProfile?: SubcampaignInfluencerProfile,
  brandProfile?: BrandProfile,
  isForBrand: boolean,
  isSubmitButtonDisabled?: boolean,
  subcampaign: Subcampaign,
  isBulkOfferActive: boolean,
  bulkOfferSubcampaigns: Array<Subcampaign>,
  submitButtonLabel?: string,
  visibleOffer: SubcampaignOffer | SubcampaignOfferForm,
  visibleDeliverables: Array<Deliverable>,
  variablePaymentsAmount: number,
  allowEditAction?: boolean,
  allowBulkEditAction?: boolean,
  onSendOffer?: () => Promise<void>,
  onSaveAsDraft?: () => Promise<void>,
  onEditClick?: () => void,
  onPasteCopiedOfferDetails?: () => void,
  onCopyOfferDetails?: () => void,
  onDownloadOffer?: () => void,
  onProvideQuoteClick?: () => void
};

class OfferCardBanner extends React.PureComponent<Props> {
  getSubcampaignOfferDisplayBudget(offer: SubcampaignOffer | undefined | null | SubcampaignOfferForm): string {
    if (!offer) {
      return '';
    }

    if (offer.type === SUBCAMPAIGN_OFFER_TYPE_QUOTE) {
      return __t('offer_card_banner.subcampaign_quote_requested');
    }

    if (offer.type === SUBCAMPAIGN_OFFER_TYPE_OFFER) {
      return `${addThousandsSeparator(offer.budget || 0)}€`;
    }

    return __t('offer_card_banner.subcampaign_other_arrangement');
  }

  hasCollapsedBulkOfferView() {
    const { isBulkOfferActive, bulkOfferSubcampaigns } = this.props;

    return isBulkOfferActive && bulkOfferSubcampaigns.length > 1;
  }

  renderContextMenu(): React.ReactNode | undefined | null {
    const {
      subcampaign,
      influencerProfile,
      allowEditAction,
      allowBulkEditAction,
      isForBrand,
      isBulkOfferActive,
      visibleOffer,
      visibleDeliverables,
      onEditClick,
      onPasteCopiedOfferDetails,
      onCopyOfferDetails,
      onDownloadOffer
    } = this.props;

    if (!isForBrand) {
      return null;
    }

    const campaignIsNotFinished = canCampaignBeChanged(subcampaign.campaign);
    const isInfluencerRejected = isInfluencerProfileRejected(influencerProfile);

    const items = [
      ...insertIf(allowBulkEditAction && campaignIsNotFinished && allowEditAction && !isBulkOfferActive, {
        name: 'bulk_edit_offer',
        type: 'modal',
        text: __t('offer_card_banner.context_menu_item'),
        meta: {
          campaignId: subcampaign.campaign.id,
          preselectedSubcampaignId: isDraft(subcampaign) ? subcampaign.id : null,
          sentSubcampaignId: isDraft(subcampaign) ? null : subcampaign.id,
          bulkOffer: {
            offer: visibleOffer,
            deliverables: visibleDeliverables
          }
        },
        extra: {
          component: BulkEditOfferModal
        }
      }),
      ...insertIf(campaignIsNotFinished && allowEditAction && !isInfluencerRejected && onEditClick, {
        name: 'change_offer',
        text: __t('offer_card_banner.change_offer_label'),
        onClick: onEditClick
      }),
      ...insertIf(onDownloadOffer, {
        name: 'download_offer',
        text: __t('offer_card_banner.download_offer_label'),
        onClick: onDownloadOffer
      }),
      ...insertIf(canCancelSubcampaign(subcampaign), {
        type: 'cancel_offer',
        name: 'cancel_offer',
        text: __t('offer_card_banner.cancel_offer_label'),
        meta: {
          subcampaign
        }
      }),
      ...insertIf(onCopyOfferDetails, {
        name: 'copy_offer_details',
        text: __t('subcampaign_offer_copy_paste.context_menu.copy_offer_details'),
        onClick: onCopyOfferDetails
      }),
      ...insertIf(!isInfluencerRejected && onPasteCopiedOfferDetails, {
        name: 'paste_offer_details',
        text: __t('subcampaign_offer_copy_paste.context_menu.paste_offer_details'),
        onClick: onPasteCopiedOfferDetails
      }),
      ...insertIf(campaignIsNotFinished && !isBulkOfferActive && !isInfluencerRejected, {
        name: 'bulk-offer',
        type: 'modal',
        text: __t('offer_card_banner.context_menu_item_send'),
        meta: {
          mode: BULK_OFFER_MODAL_MODE_CREATE,
          campaignId: subcampaign.campaign.id,
          preselectedSubcampaignId: isDraft(subcampaign) ? subcampaign.id : null,
          sentSubcampaignId: isDraft(subcampaign) ? null : subcampaign.id,
          subcampaign,
          bulkOffer: {
            offer: visibleOffer,
            deliverables: visibleDeliverables
          }
        },
        extra: {
          component: BulkOfferModal
        }
      })
    ];

    if (items.length === 0) {
      return null;
    }

    return (
      <div className="offer-card-banner__context-menu">
        <MoreButtonWithMenu
          width={6}
          iconWidth={6}
          iconHeight={24}
          contextMenuProps={{ items }}
        />
      </div>
    );
  }

  renderStatus(): React.ReactNode | undefined | null {
    const {
      subcampaign,
      visibleOffer,
      isBulkOfferActive,
      triggersOfferUpdate
    } = this.props;

    if (!subcampaign || !visibleOffer) {
      return null;
    }

    if (isBulkOfferActive) {
      return (
        <StatusLabel
          size="nano"
          label={__t('offer_card_banner.subcampaign_state_label_draft')}
          type={SUBCAMPAIGN_STATE_TO_LABEL_TYPE.draft}
        />
      );
    }

    if (triggersOfferUpdate) {
      return null;
    }

    if (isRevoked(subcampaign)) {
      return (
        <StatusLabel
          size="nano"
          label={__t(`offer_card_banner.subcampaign_state_label_revoked_by_${String(subcampaign.revokedBy)}`)}
          type={SUBCAMPAIGN_STATE_TO_LABEL_TYPE.revoked}
        />
      );
    }

    if (isNegotiationsReopened(subcampaign)) {
      return (
        <StatusLabel
          size="nano"
          label={__t('offer_card_banner.negotiations_reopened_label')}
          type="warning"
        />
      );
    }

    return (
      <StatusLabel
        size="nano"
        label={__t(`offer_card_banner.subcampaign_state_label_${visibleOffer.state}`)}
        type={SUBCAMPAIGN_STATE_TO_LABEL_TYPE[String(visibleOffer.state)]}
      />
    );
  }

  renderAvatar = (entity: Entity | null, customSize?: ImageSize): React.ReactNode => (
    <div
      className="offer-card-banner__avatar"
      key={entity?.id}
    >
      <Popover
        triggerComponent={(
          <EntityAvatar
            key={entity?.id}
            entity={entity}
            link={entity?.url}
            customSize={{ height: customSize?.height ?? 48, width: customSize?.width ?? 48 }}
            openInNewTab
            hideTitle
            bordered
          />
        )}
      >
        {this.renderLink(entity)}
      </Popover>
    </div>
  );

  renderName(entity: Entity | null): React.ReactNode {
    const { influencerProfile, isForBrand } = this.props;
    let showVerificationLabel = isForBrand;

    if (this.hasCollapsedBulkOfferView()) {
      showVerificationLabel = false;
    }

    const classes = classnames('offer-card-banner__name', {
      'offer-card-banner__name--brand-profile': !isForBrand
    });

    return (
      <div className={classes}>
        <EntityName
          className="text-overflow-ellipsis"
          entity={entity}
          link={entity?.url}
          openInNewTab
        />
        {
          showVerificationLabel && influencerProfile && (
            <InfluencerVerificationLabel
              withMargin
              type={influencerProfile.isLightweight ? 'unverified' : 'verified'}
            />
          )
        }
      </div>
    );
  }

  renderLink = (entity: Entity | null): React.ReactNode => (
    <a
      href={entity?.url}
      className="popover-scrollable__link offer-card-banner__link"
      target="_blank"
      rel="noopener noreferrer"
    >
      {entity?.displayName}
    </a>
  );

  renderChannels(): React.ReactNode | undefined | null {
    const { bulkOfferSubcampaigns, influencerProfile } = this.props;

    if (this.hasCollapsedBulkOfferView()) {
      const metricsChannels = CHANNELS.map((channel: ChannelTypes) => prepareAggregatedMetricsData(bulkOfferSubcampaigns, channel));

      return (
        <MetricsChannelsBasic
          channels={metricsChannels}
          showChannels={CHANNELS}
          maxChannels={2}
          type="limited"
          includeRatio
          hideIconPopover
        />
      );
    }

    if (influencerProfile && influencerProfile.channels) {
      const metricsChannels = CHANNELS.map((channel: ChannelTypes) => prepareDataForMetricsChannelsBasic(influencerProfile, channel));

      return (
        <MetricsChannelsBasic
          channels={metricsChannels}
          showChannels={CHANNELS}
          maxChannels={2}
          type="limited"
          includeRatio
        />
      );
    }

    return null;
  }

  renderTasks(): React.ReactNode {
    const { visibleDeliverables } = this.props;

    return (
      <div className="offer-card-banner__tasks">
        <span className="offer-card-banner__label">
          {__t('offer_card_banner.deliverables_label')}
        </span>
        <div className="offer-card-banner__counters">
          {
            visibleDeliverables.length ?
              <DeliverableTypeCounters deliverables={visibleDeliverables} /> :
              '-'
          }
        </div>
      </div>
    );
  }

  renderOffer(): React.ReactNode {
    const { visibleOffer } = this.props;

    return (
      <div className="offer-card-banner__offer">
        <div className="offer-card-banner__information-row">
          <span className="offer-card-banner__label">
            {__t('offer_card_banner.offer_label')}
          </span>
          {
            isNil(visibleOffer.type) ?
              __t('offer_card_banner.na_offer') :
              this.getSubcampaignOfferDisplayBudget(visibleOffer)
          }
        </div>
        {this.renderVariablePayments()}
      </div>
    );
  }

  renderVariablePayments(): React.ReactNode {
    const { variablePaymentsAmount } = this.props;

    if (!variablePaymentsAmount) {
      return null;
    }

    return (
      <div className="offer-card-banner__variable-payments">
        <div className="offer-card-banner__information-row">
          <span className="offer-card-banner__label">
            {__t('offer_card_banner.variable_payment')}
          </span>
          <span className="offer-card-banner__value">
            {renderPrice(variablePaymentsAmount)}
          </span>
        </div>
      </div>
    );
  }

  renderBulkOfferView() {
    const { bulkOfferSubcampaigns } = this.props;

    return (
      <div className="offer-card-banner__content--bulk">
        <div className="offer-card-banner__bulk-header">
          <span className="offer-card-banner__bulk-title">
            {bulkOfferSubcampaigns.length} {__t('offer_card_banner.label_creator', { withPlural: bulkOfferSubcampaigns.length })}
          </span>
          {this.renderChannels()}
        </div>
        {this.renderSelectedInfluencers()}
      </div>
    );
  }

  renderSelectedInfluencers() {
    const { isBulkOfferActive, bulkOfferSubcampaigns } = this.props;

    const bulkOfferSubcampaignsCount = bulkOfferSubcampaigns.length;
    const collapsedCount = bulkOfferSubcampaignsCount - MAX_INFLUENCER_COUNT;
    const showCollapseControl = collapsedCount > 0;

    return isBulkOfferActive && bulkOfferSubcampaignsCount > 1 && (
      <div className="offer-card-banner__influencers">
        {
          bulkOfferSubcampaigns.slice(0, MAX_INFLUENCER_COUNT).map((subcampaign) => {
            const entity = getEntityFromProfile(subcampaign.creatorProfile);
            return this.renderAvatar(entity, { width: 24, height: 24 });
          }, this)
        }
        {
          showCollapseControl && (
            <PopoverScrollable
              withFixedVerticalScroll
              list={
                bulkOfferSubcampaigns.slice(MAX_INFLUENCER_COUNT).map((subcampaign) => {
                  const entity = getEntityFromProfile(subcampaign.creatorProfile);

                  return {
                    text: entity?.displayName ?? '',
                    url: entity?.url
                  };
                })
              }
              size="small"
              trigger={(
                <div className="offer-card-banner__influencers-count">
                  +{collapsedCount}
                </div>
              )}
            />
          )
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const {
      canEdit,
      isForBrand,
      influencerProfile,
      brandProfile,
      subcampaign,
      isSubmitButtonDisabled,
      submitButtonLabel,
      onSaveAsDraft,
      onSendOffer,
      onEditClick,
      allowEditAction,
      isBulkOfferActive,
      onProvideQuoteClick
    } = this.props;

    if (isNil(influencerProfile) || isNil(brandProfile)) {
      return null;
    }

    const entity = getEntityFromProfile(isForBrand ? influencerProfile : brandProfile);
    const managedByBrand = getManagedByBrand(brandProfile);
    const isBulkOffer = this.hasCollapsedBulkOfferView();
    const classes = classnames('offer-card-banner', {
      'offer-card-banner--bulk': isBulkOffer
    });

    const contentClasses = classnames('offer-card-banner__content', {
      'offer-card-banner__content--creator': !isForBrand
    });

    const creatorClasses = classnames('offer-card-banner__creator', {
      'offer-card-banner__creator--centered': !isForBrand
    });

    return (
      <PageSection
        style="volume"
        padding="none"
        margin="none"
        border="none"
      >
        <div className={classes}>
          {
            isBulkOffer ?
              this.renderBulkOfferView() :
              <React.Fragment>
                <div className={creatorClasses}>
                  {this.renderAvatar(entity)}
                  <div className="offer-card-banner__info">
                    {this.renderName(entity)}
                    {isForBrand && this.renderChannels()}
                    {
                      !isForBrand && brandProfile && managedByBrand && (
                        <ManagedByTag managedByBrand={managedByBrand} />
                      )
                    }
                  </div>
                </div>
                {this.renderContextMenu()}
                <div className={contentClasses}>
                  {this.renderOffer()}
                  {this.renderTasks()}
                </div>
                <div className="offer-card-banner__status">
                  {this.renderStatus()}
                </div>
              </React.Fragment>
          }

          <div className="offer-card-banner__buttons">
            <OfferCardFooter
              onSendOffer={onSendOffer}
              onSaveAsDraft={onSaveAsDraft}
              canEdit={canEdit}
              influencerProfile={influencerProfile}
              brandProfile={brandProfile}
              subcampaign={subcampaign}
              isBulkOfferActive={isBulkOfferActive}
              isForBrand={isForBrand}
              isSubmitButtonDisabled={isSubmitButtonDisabled}
              submitButtonLabel={submitButtonLabel}
              onEditClick={onEditClick}
              onProvideQuoteClick={onProvideQuoteClick}
              allowEditAction={!!allowEditAction}
              layout="horizontal"
            />
          </div>
        </div>
      </PageSection>
    );
  }
}

export default OfferCardBanner;
